import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import {
  ClassAndSubjectPopup,
  ConductionButtonsComponent,
  HeaderIconProps,
  HeaderIconWrapper,
  HeaderIconWrapperV2,
  HomeVideoCard,
  IClassAndSubjectSelected,
  IStyles,
  ImageWrapper,
  ModuleButton,
  PrimaryButton,
  SecondaryButton,
  cloneObject,
  deserify,
  findOfflineUserRequestByParamsDexie,
  getEnvConfig,
  getLocalStorage,
  migrateIndexedDbData,
  pxToRem,
  pxTovW,
  setLocalStorage,
  storeOfflineAccessKeyInCache,
  useCommonServiceClientContext,
  useFeatureEnabled,
} from '@geneo2-web/shared-ui';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ANALYTICS_CLASS_SELECTION,
  ASSESMENT_LIBRARY,
  ASSESSMENT_CREATE,
  FORMATIVE,
  HOME,
  HOMEWORK_CHAPTER_SELECTION,
  HOMEWORK_MANAGE,
  QPG,
  REPORT,
  SSO_REDIRECT,
  TEACH_CHAPTER_SELECTION,
} from '../../routeHandling/RoutesNomenclature';

import CloseIcon from '@mui/icons-material/Close';
import { Feature } from '@protos/school_management/school.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { v4 as uuidv4 } from 'uuid';
import { useConnectedClassContext } from '../../app/Context/ConnectedClassContextProvider';
import { useDownloadContext } from '../../app/Context/DownloadContextProviderV2';
import { useGlobalContext } from '../../app/Context/GlobalContextProvider';
import { useAppDispatch, useAppSelector } from '../../reduxStore/reduxHooks';
import { subjectsWithClass } from '../../utils/icons';
import { interactionEvent } from '../Auth/auth.events';
import { setIsLoggedInOffline, setUserInfo } from '../Auth/reducer/auth.slice';
import { aiHomeworkCloseEvent } from '../Homework/homework.events';
import { resetConnectedClassState } from '../Teach/reducer/connectedClass.slice';
import {
  resetTeachState,
  setCustomResourceData,
  setSelectedResourceSessionId,
} from '../Teach/reducer/teach.slice';
import {
  resetCustomHomework,
  resetDraftUploadedFiles,
  resetAssessmentInstructions,
  setAssessmentCreationType,
  resetAssessmentFlowState,
  setQPGContent,
  setQPGSettingsConfig,
  setQPGFormatDetails,
  setFaFormatDetails,
  setFAContent,
} from '../Assessment/reducer/assessmentFlow.slice';
import ConfigSelector from '../OfflineScreens/Teach/components/ConfigSelector';
import {
  aiChapterCloseEvent,
  aiLessonCloseEvent,
  aiTopicCloseEvent,
} from '../Teach/teach.events';
import FeatureList, { FeatureType } from './components/FeatureList';
import HomeHomeworksList from './components/HomeHomeworksList';
import HomeLessonPlansList from './components/HomeLessonPlansList';
import { TeacherHomeMobileCarousel } from './components/TeacherHomeMobileCarousel';
import { TeacherHomeWebCarousel } from './components/TeacherHomeWebCarousel';
import { aiBookCloseEvent, aiBookOpenEvent } from './home.events';
import {
  setClassAndSubjectInfo,
  setTeacherCompletedHwList,
  setTeacherHwList,
  setTeacherProfileData,
} from './reducer/homeDashboard.slice';
import {
  SchoolBoardMediumInfo,
  TeacherLoginResponseType,
} from '@protos/user_management/ums.login.apis_pb';
import { resetReportState } from '../Reports/reducer/reports.slice';
import HomeCompletedHomeworkList from './components/HomeCompletedAssessmentList';
import {
  AssessmentEnum,
  ModeOfConductionEnum,
} from '@protos/learning_management/lms.db_pb';
import {
  resetManageHWState,
  setAssessmentListData,
  setModeOfConduction,
} from '../ManageHomework/reducer/manageHomework.slice';
import AssementSelectionPopup from '../ManageHomework/ManageHomeworkHome/AssementSelectionPopup';
import HomeClickerAssessmentList from './components/HomeClickerAssessmentList';
import { resetClickerAssessmentState } from '../Assessment/ClickerAssessment/reducer/clickerAssessment.slice';
import { getModeOfConductionEnum } from '../../utils/functions';
import { PreviousYearQPCard } from './components/PreviousYearQPCard';
import { QuestionPaperType } from '@protos/content_management/content.db_pb';
import { QPGSelectMultiSubjectsPopup } from '../ManageHomework/components/QPGSelectMultiSubjectsPopup';
import { FASelectMultiSubjectsPopup } from '../Assessment/FormativeAssessmentV2/FASelectMultiSubjectsPopup/FASelectMultiSubjectsPopup';

const styles: IStyles = {
  root: {},
  mainContainer: {
    padding: {
      md: `${pxTovW(40)} ${pxTovW(40)}`,
      lg: `${pxTovW(200)} ${pxTovW(240)}`,
    },
    paddingTop: { md: pxToRem(120), lg: pxTovW(184) },
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: { md: pxTovW(42) },
    paddingBottom: { xs: pxToRem(20), md: pxTovW(32) },
  },
  logo: {
    cursor: 'pointer',
    width: { xs: pxToRem(162), md: pxTovW(260) },
    height: { xs: pxToRem(46), md: pxTovW(73) },
  },
};

export const Home = () => {
  const location = useLocation();
  const token = deserify(
    useAppSelector((state) => state.auth.user_info?.token)
  );
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [assessmentListLoading, setAssessmentListLoading] = useState(false);
  const [assessmentListError, setAssessmentListError] =
    useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { user_info, is_logged_in_offline, sso_data } = deserify(
    useAppSelector((state) => state.auth)
  );
  const android = window.Android;
  const config = getEnvConfig();
  const { class_subject_info } = useAppSelector((state) => state.homeDashboard);
  const [currentRoute, setCurrentRoute] = useState('');
  const teacher_profile_id = deserify(getLocalStorage('userId'));
  const { isOffline, isMigratingData, setIsMigratingData } =
    useDownloadContext();

  // if we access the page /home?offline=true we are forcing the frontend to open the homepage in offline mode
  const forcedOffline =
    new URLSearchParams(location.search).get('offline') === 'true';

  const isOfflineFrontend = isOffline || forcedOffline || is_logged_in_offline;

  const {
    UMSLoginAPIServiceV1ClientWithStatusCodeHandler,
    UmsUtilityAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler,
    ContentParserAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();

  const { chapter_resources, selected_topic_info, selected_lessons_info } =
    deserify(useAppSelector((state) => state.teach));
  const { teacher_profile_data } = deserify(
    useAppSelector((state) => state.homeDashboard)
  );
  const { selected_assessment_id } = deserify(
    useAppSelector((state) => state.manageHomework)
  );
  const featureInfo = user_info?.schoolDetails[0]?.featuresPlanInfo;
  const isWhiteLabelingFeature = useFeatureEnabled(
    featureInfo,
    Feature.WHITE_LABELING
  );
  const { resetConnectedClass } = useConnectedClassContext();
  const [assessmentSelectionPopupDisplay, setAssessmentSelectionPopupDisplay] =
    useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<
    AssessmentEnum | undefined
  >();
  const [createflow, setCreateFlow] = useState(false);

  const [selectedMode, setSelectedMode] = useState<
    'Geneo App' | 'Geneo Clicker' | ''
  >('');
  const [faMultiSubjectsPopup, setFAMultiSubjectsPopup] =
    useState<boolean>(false);
  const [qpgSelectMultiSubjectsPopup, setQPGSelectMultiSubjectsPopup] =
    useState<boolean>(false);
  const [isOpenSelectModeOfConduction, setIsOpenSelectModeOfConduction] =
    useState(false);
  const [navigateToLibrary, setNavigateToLibrary] = useState(false);
  const isClickerEnabled = class_subject_info?.isClickerSupported;
  const [hasPreviousYearQP, setHasPreviousYearQP] = useState(false);

  const { setSelectedFunction, setSelectedLogoClickHandler } =
    useGlobalContext();
  const backButtonClick = async () => {
    navigate(HOME);
  };

  // ------------------------------------------------------------------

  useEffect(() => {
    if (!isOffline && is_logged_in_offline) {
      offlineLogin();
    }
  }, [isOffline]);

  // ------------------------------------------------------------------

  const offlineLogin = async () => {
    if (!user_info) {
      return;
    }
    const offlineUser = await findOfflineUserRequestByParamsDexie({
      username: user_info?.userName,
    });
    if (!offlineUser) {
      return;
    }
    const res2 =
      await UmsUtilityAPIServiceV1ClientWithStatusCodeHandler.validateLoginSession(
        {
          userName: offlineUser?.username,
          verificationCode: offlineUser?.passwordHash,
          role: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
        }
      );
    setLocalStorage('activeSession', res2.activeSessionId.toString());
    dispatch(setIsLoggedInOffline(false));
    // window.location.reload();
  };

  // ------------------------------------------------------------------

  useEffect(() => {
    dispatch(resetAssessmentInstructions());
    dispatch(resetAssessmentFlowState());
    dispatch(resetReportState());
    dispatch(resetConnectedClassState());
    dispatch(resetClickerAssessmentState());
    dispatch(resetTeachState());
    resetConnectedClass();
    dispatch(resetCustomHomework());
    dispatch(resetDraftUploadedFiles());
    migrateIndexedDb();
    dispatch(setCustomResourceData(undefined));
    dispatch(resetManageHWState());
    (async () => {
      await aiLessonCloseEvent({
        lessonId: selected_lessons_info?.lessonId,
        isOffline: isOffline,
      });
      await aiTopicCloseEvent({
        topicId: selected_topic_info?.topicId,
        isOffline: isOffline,
      });
      await aiChapterCloseEvent({
        chapterId: chapter_resources?.chapterId,
        isOffline: isOffline,
      });
      await aiHomeworkCloseEvent({
        homeworkId: selected_assessment_id,
        isOffline: isOffline,
      });
      dispatch(setSelectedResourceSessionId(undefined));
    })();
    setSelectedFunction(() => backButtonClick);
    setSelectedLogoClickHandler(null);

    return () => {
      setSelectedFunction(null);
    };
  }, []);

  // ------------------------------------------------------------------

  const getTeacherAssessmentList = async (teacherId: string) => {
    try {
      setAssessmentListLoading(true);
      setAssessmentListError(false);
      // const response = await LmsTeacherHWAPIsSer({
      //   teacherId: BigInt(teacherId),
      // });
      // const response =
      //   await LmsHomewokTeacherAPIServiceV1ClientWithStatusCodeHandler.getTeacherHomeworkList(
      //     {
      //       teacherId: BigInt(teacherId),
      //       sectionId: class_subject_info?.sectionId,
      //       subjectId: class_subject_info?.subjectId,
      //     }
      //   );
      const response =
        await LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler.getTeacherAssessmentsList(
          {
            teacherId: BigInt(teacherId),
            subjectId: class_subject_info?.subjectId,
            sectionId: class_subject_info?.sectionId,
            assessmentType: AssessmentEnum.ASSESSMENT_UNDEFINED,
          }
        );
      const data = response.assessmentList;
      if (data?.assessmentList) {
        const hw_data = data.assessmentList;
        dispatch(setAssessmentListData(hw_data));
        const Completedlist = [
          ...data.assessmentList.ended,
          // ...data.homeworkList.ended,
        ];
        const Assignedlist = [
          ...data.assessmentList.assigned,
          // ...data.homeworkList.ended,
        ];
        // dispatch(setAssessmentListData(data.assessmentList));
        // const list = [
        //   ...data.assessmentList.assigned,
        //   ...data.assessmentList.ended,
        // ];

        //even after sending a subject id as payload all the homeworks are received,
        //even if we use filter at our end then only few combination of (subject and class) have data
        dispatch(setTeacherHwList(Assignedlist));
        dispatch(setTeacherCompletedHwList(Completedlist));
        // setHomeworkList(list);
      } else {
        dispatch(setAssessmentListData(undefined));
        dispatch(setTeacherHwList(undefined));
        dispatch(setTeacherCompletedHwList(undefined));
        setAssessmentListError(true);
        // setHomeworkList([]);
      }
      setAssessmentListLoading(false);
    } catch (err) {
      setAssessmentListLoading(false);
      setAssessmentListError(true);
      dispatch(setTeacherCompletedHwList(undefined));
      dispatch(setTeacherHwList(undefined));
      console.log(err);
    }
  };

  // ------------------------------------------------------------------

  useEffect(() => {
    fetchTeacherProfile();
    areThereAnyPreviousYearQP();
  }, []);

  // ------------------------------------------------------------------

  useEffect(() => {
    {
      getTeacherAssessmentList(teacher_profile_id);
    }
  }, [class_subject_info]);

  // ------------------------------------------------------------------

  useEffect(() => {
    if (location.state?.from) {
      if (location.state.from.includes(SSO_REDIRECT)) {
        if (
          sso_data?.is_sso_user &&
          sso_data?.sso_subject_ids?.length &&
          sso_data?.session_mode
        ) {
          if (sso_data.session_mode === 'teach') {
            dashBoardrouting(
              `${TEACH_CHAPTER_SELECTION}/${
                class_subject_info?.subjectId ?? 'undefined'
              }`
            );
          } else if (sso_data.session_mode === 'assess') {
            // assessClickHandler();
            navigate(HOMEWORK_MANAGE);
          }
        }
      }
    }
  }, [location.state?.from]);

  // ------------------------------------------------------------------

  const migrateIndexedDb = async () => {
    if (isMigratingData) {
      return;
    }
    setIsMigratingData(true);
    try {
      await migrateIndexedDbData();
    } catch (err) {
      console.error('Error while migrating data');
    }
    setIsMigratingData(false);
  };
  async function fetchTeacherProfile() {
    try {
      const response =
        await UMSLoginAPIServiceV1ClientWithStatusCodeHandler.fetchTeacherProfile(
          {
            teacherProfileId: teacher_profile_id,
          }
        );
      const resp = response.data;
      if (resp) {
        const accessKey = resp.schoolDetails[0]?.schoolId.toString()
          ? 'school-' + resp.schoolDetails[0].schoolId.toString()
          : 'geneo';
        await storeOfflineAccessKeyInCache(accessKey);
        if (token) {
          resp.token = token;
        }
        const resposne2 = cloneObject(resp);
        dispatch(setTeacherProfileData(resposne2));
        dispatch(setUserInfo(resposne2));
      } else {
        dispatch(setTeacherProfileData(undefined));
        dispatch(setUserInfo(undefined));
      }
    } catch (err) {
      dispatch(setTeacherProfileData(undefined));
    }
  }

  // useEffect(() => {
  //   //geneo.ums.login.UMSLoginAPIServiceV1/fetchTeacherProfile
  // }, []);

  //^ SubjectPopup
  const [subjectPopupState, setSubjectPopupState] = useState(false);

  const dashBoardrouting = async (route: string) => {
    if (!class_subject_info) {
      setSubjectPopupState(true);
      try {
        if (deviceType === 'android' && android?.disableAppRefresh) {
          console.log('App refresh disabled on Android for home page');
          android.disableAppRefresh();
        }
      } catch (error) {
        console.log(
          'An error occurred while attempting to enable app refresh on Android:'
        );
      }
      setCurrentRoute(route);
    } else {
      // Redirect if offline
      if (isOfflineFrontend) {
        navigate('/offline' + route);
        return;
      }
      navigate(route);
    }
  };

  const assessClickHandler = () => {
    if (!class_subject_info) {
      setSubjectPopupState(true);
      setCreateFlow(true);
      try {
        if (deviceType === 'android' && android?.disableAppRefresh) {
          console.log('App refresh disabled on Android for home page');
          android.disableAppRefresh();
        }
      } catch (error) {
        console.log(
          'An error occurred while attempting to enable app refresh on Android:'
        );
      }
    } else {
      setAssessmentSelectionPopupDisplay(true);
      setCreateFlow(false);
    }
  };

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  const commonInteractionEvent = async (context: string, name: string) => {
    await interactionEvent({
      context: context,
      name: name,
      pathSegments: pathSegments,
      isOffline: isOffline,
    });
  };

  const featureList: FeatureType[] = [
    {
      fileName: 'book',
      type: 'png',
      cardText: 'Create your Assessment in 2 Minutes',
      bgColor: '#EEFFB3',
      onClick: () => {
        commonInteractionEvent('Features', 'CREATE_YOUR_HOMEWORK');
        dashBoardrouting(
          `${HOMEWORK_CHAPTER_SELECTION}/${class_subject_info?.subjectId}`
        );
      },
      isDisabled: isOfflineFrontend,
    },
    {
      fileName: 'provision',
      type: 'png',
      cardText: 'Curriculum Aligned Teaching Resources',
      bgColor: '#DAF5FF',
      onClick: () => {
        commonInteractionEvent('Features', 'CURRICULUM_TEACHING_RESOURCES');
        dashBoardrouting(
          `${TEACH_CHAPTER_SELECTION}/${class_subject_info?.subjectId}`
        );
      },
    },
    {
      fileName: 'homework',
      type: 'png',
      cardText: 'Know Your Class (Pre-Assessment)',
      bgColor: '#FFEEEE',
      onClick: () => {
        commonInteractionEvent('Features', 'CLASS_ANALYSIS');
        navigate(ANALYTICS_CLASS_SELECTION);
      },
      isDisabled: isOfflineFrontend,
    },
  ];

  const headerIconList: HeaderIconProps[] = [
    {
      fileName: 'teach-new',
      type: 'png',
      cardText: 'Teach',
      onClick: () => {
        commonInteractionEvent('Navigation_bar', 'TEACH');
        dashBoardrouting(
          `${TEACH_CHAPTER_SELECTION}/${class_subject_info?.subjectId}`
        );
      },
      isHidden: sso_data?.session_mode === 'assess',
    },
    // {
    //   fileName: 'new-hw',
    //   type: 'png',
    //   cardText: 'New Homework',
    //   onClick: () => {
    //     commonInteractionEvent('Navigation_bar', 'NEW_HOMEWORK');
    //     dashBoardrouting(
    //       `${HOMEWORK_CHAPTER_SELECTION}/${class_subject_info?.subjectId}`
    //     );
    //   },
    //   isDisabled: isOfflineFrontend,
    // },
    // {
    //   fileName: 'manage-hw',
    //   type: 'png',
    //   cardText: 'Manage Homework',
    //   onClick: () => {
    //     commonInteractionEvent('Navigation_bar', 'MANAGE_HOMEWORK');
    //     navigate(HOMEWORK_MANAGE);
    //   },
    //   isDisabled: isOfflineFrontend,
    // },
    {
      fileName: 'assess-new',
      type: 'png',
      cardText: 'Assess',
      onClick: () => {
        commonInteractionEvent('Navigation_bar', 'ASSESS');
        navigate(HOMEWORK_MANAGE);
      },
      isDisabled: isOfflineFrontend,
      isHidden: sso_data?.session_mode === 'teach',
    },
    {
      fileName: 'anlyse-new',
      type: 'png',
      cardText: 'Analysis',
      onClick: () => {
        commonInteractionEvent('Navigation_bar', 'ANALYSIS');
        navigate(ANALYTICS_CLASS_SELECTION);
      },
      isDisabled: isOfflineFrontend,
    },
  ];

  const pathSegments = location.pathname.split('/');
  const classAndsubjectClickHandler = async (
    inputClassInfo: IClassAndSubjectSelected
  ) => {
    await interactionEvent({
      context: 'Subject_&_Class_Selection',
      name: inputClassInfo.subject.toUpperCase(),
      pathSegments: pathSegments,
      isOffline: isOffline,
    });
    if (class_subject_info)
      await aiBookCloseEvent({
        bookId: class_subject_info?.bookId,
        isOffline: isOffline,
      });
    await aiBookOpenEvent({
      bookId: inputClassInfo.bookId,
      bookSessionId: uuidv4(),
      isOffline: isOffline,
    });

    dispatch(setClassAndSubjectInfo(inputClassInfo));
    setSubjectPopupState(false);
    if (currentRoute) {
      if (currentRoute.includes('undefined')) {
        const newRoute = currentRoute.replace(
          'undefined',
          inputClassInfo.subjectId.toString()
        );
        // Redirect if offline
        if (isOfflineFrontend) {
          navigate('/offline' + newRoute);
          return;
        }
        navigate(newRoute);
      } else {
        navigate(currentRoute);
      }
    } else {
      if (createflow) {
        setAssessmentSelectionPopupDisplay(true);
        setCreateFlow(false);
      } else if (navigateToLibrary) {
        setIsOpenSelectModeOfConduction(true);
      }
    }
    setCurrentRoute('');
  };

  function getDeviceType() {
    const userAgent = navigator.userAgent;
    if (/Android/i.test(userAgent)) {
      return 'android';
    } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
      return 'ios';
    } else {
      return 'web';
    }
  }
  const optionSelectionHandler = (assessmentType: AssessmentEnum) => {
    setSelectedAssessment(assessmentType);
    dispatch(
      setAssessmentCreationType({
        asessmentType: assessmentType,
        creationType: 'Create',
        modeOfConduction:
          selectedMode === '' && class_subject_info?.isClickerSupported
            ? ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
            : selectedMode === 'Geneo Clicker'
            ? ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
            : ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_APP,
      })
    );
  };
  const createAssessmentNavigation = async () => {
    // if (selectedMode) {
    //   dispatch(setModeOfConduction(getModeOfConductionEnum(selectedMode)));
    // }

    dispatch(
      setModeOfConduction(
        selectedMode === '' && class_subject_info?.isClickerSupported
          ? ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
          : selectedMode === 'Geneo Clicker'
          ? ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
          : ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_APP
      )
    );

    switch (selectedAssessment) {
      case AssessmentEnum.ASSESSMENT_HOMEWORK:
        navigate(
          `${HOMEWORK_CHAPTER_SELECTION}/${class_subject_info?.subjectId}?home=true`
        );
        break;
      // case AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT:
      //   navigate(
      //     `${FORMATIVE}${ASSESSMENT_CREATE}/${class_subject_info?.subjectId}?home=true`
      //   );
      //   break;
      case AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT:
        dispatch(
          setFaFormatDetails({
            ...(class_subject_info && {
              selectedClass: {
                id: class_subject_info.classId,
                name: `Class ${class_subject_info.classname}`,
              },
            }),
            selectedSubjects: [{ name: '', id: 0 }],
          })
        );
        dispatch(setFAContent(undefined));
        setAssessmentSelectionPopupDisplay(false);
        setFAMultiSubjectsPopup(true);
        break;
      case AssessmentEnum.ASSESSMENT_QUESTION_PAPER:
        // navigate(
        //   `${QPG}${ASSESSMENT_CREATE}/${class_subject_info?.subjectId}`,
        //   { state: { redirectionPath: location.pathname } }
        // );
        dispatch(
          setQPGFormatDetails({
            ...(class_subject_info && {
              selectedClass: {
                id: class_subject_info.classId,
                name: `Class ${class_subject_info.classname}`,
              },
            }),
            selectedSubjectsAndMarks: [
              { subject: { name: '', id: 0 }, marks: undefined },
            ],
            selectedTemplates: [{ name: '', id: 0 }],
          })
        );
        dispatch(setQPGContent(undefined));
        setQPGSelectMultiSubjectsPopup(true);
        break;
      default:
        return;
    }
    await interactionEvent({
      context: 'Choose_type_of_assessment',
      name: 'PROCEED',
      pathSegments: pathSegments,
      isOffline: isOffline,
    });
  };

  // Check device type
  const deviceType = getDeviceType();

  // ------------------------------------------------------------------

  // const canWeShowClickerAssessments = () => {
  //   const userIsOnline = !isOfflineFrontend;
  //   const subjectInfoAllowsClicker =
  //     !class_subject_info || class_subject_info.isClickerSupported !== false;
  //   const atLeastOneClassSupportsClicker = user_info?.teachClassSubjects.some(
  //     (item) => item.isClickerSupported === true
  //   );

  //   return (
  //     userIsOnline && subjectInfoAllowsClicker && atLeastOneClassSupportsClicker
  //   );
  // };
  const canWeShowClickerAssessments = () => {
    const userIsOnline = !isOfflineFrontend;
    if (!userIsOnline) return false;
    if (class_subject_info && class_subject_info.isClickerSupported === true) {
      return true;
    }
    const atLeastOneClassSupportsClicker = user_info?.teachClassSubjects?.some(
      (item) => item.isClickerSupported === true
    );
    if (!class_subject_info && atLeastOneClassSupportsClicker) {
      return true;
    }
    return false;
  };

  // ------------------------------------------------------------------

  const areThereAnyPreviousYearQP = async () => {
    try {
      const response =
        await ContentParserAPIServiceV1ClientWithStatusCodeHandler.getQuestionPapers(
          {
            questionPaperType: QuestionPaperType.PREVIOUS_YEAR,
            teacherId: BigInt(teacher_profile_id),
          }
        );
      setHasPreviousYearQP(response.totalQuestionPapers > 0);
    } catch (err) {
      console.log(err);
      setHasPreviousYearQP(false);
    }
  };

  // ------------------------------------------------------------------

  return (
    <Box>
      {mobile && (
        <Box sx={{ p: `${pxToRem(20)} ${pxToRem(20)} 0` }}>
          <SecondaryButton
            witharrow={class_subject_info?.subject ? false : true}
            onClick={() => {
              setSubjectPopupState(true);
              try {
                if (deviceType === 'android' && android?.disableAppRefresh) {
                  console.log('App refresh disabled on Android for home page');
                  android.disableAppRefresh();
                }
              } catch (error) {
                console.log(
                  'An error occurred while attempting to enable app refresh on Android:'
                );
              }
            }}
            // sx={{ position: 'relative', width: '100%' }}
          >
            {class_subject_info && class_subject_info?.classname ? (
              <Typography
                textAlign={'left'}
                width={'100%'}
                variant="button"
              >{`${class_subject_info?.classname} ${class_subject_info?.section} - ${class_subject_info?.subject}`}</Typography>
            ) : (
              <Typography variant="button">Choose Class & Subject</Typography>
            )}
            {class_subject_info?.subject && (
              <CloseIcon
                sx={{
                  position: 'absolute',
                  right: '10px',
                  display: 'inline-block',
                  fontSize: { xs: pxToRem(14), md: pxTovW(18) },
                }}
                onClick={(ev) => {
                  ev.stopPropagation();
                  dispatch(setClassAndSubjectInfo(undefined));
                  try {
                    if (deviceType === 'android' && android?.enableAppRefresh) {
                      android.enableAppRefresh();
                      console.log(
                        'App refresh enabled on Android for home page'
                      );
                    }
                  } catch (error) {
                    console.log(
                      'An error occurred while attempting to enable app refresh on Android:'
                    );
                  }
                }}
              />
            )}
          </SecondaryButton>
        </Box>
      )}

      <Box sx={{ position: { md: 'fixed' }, width: '100vw', zIndex: 1 }}>
        {/* <HeaderIconWrapper icons={headerIconList} /> */}
        <HeaderIconWrapperV2 icons={headerIconList} />
      </Box>

      <Box sx={styles.mainContainer}>
        {isOfflineFrontend && (
          <Box
            sx={{
              marginTop: { xs: pxToRem(10), md: pxTovW(20) },
              marginBottom: { xs: pxToRem(10), md: pxTovW(10) },
            }}
          >
            <ConfigSelector />
          </Box>
        )}

        {/* {!isOfflineFrontend && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              backgroundColor: '#F3F4F6', // light gray
              padding: {
                xs: `${pxToRem(10)} ${pxToRem(20)}`,
                md: `${pxTovW(20)} ${pxTovW(30)}`,
              },
              borderRadius: pxToRem(8),
              width: 'fit-content',
              mx: 'auto', // center horizontally
              mb: { xs: pxToRem(10), md: pxTovW(20) },
              mt: { xs: pxToRem(20), lg: pxTovW(10) },
              cursor: 'pointer',
            }}
            onClick={() => {
              dashBoardrouting(`${REPORT}`);
            }}
          >
            <ImageWrapper
              name="document"
              type="png"
              parentFolder="icons"
              styles={{
                width: pxToRem(30),
                height: pxToRem(30),
              }}
            />
            <Typography variant="bodyText" fontWeight={'bold'}>
              Report Center
            </Typography>
          </Box>
        )} */}
        <Box>
          {!isOfflineFrontend && (
            <Box
              sx={{
                width: { xs: '90%', md: '100%' },
                display: 'flex',
                overflowX: 'auto',
                justifyContent: { xs: 'flex-start', md: 'center' },
                '&::-webkit-scrollbar': { display: 'none' },
                gap: { xs: pxToRem(13), md: pxTovW(20) },
                marginX: 'auto',
              }}
            >
              <ModuleButton
                title="Report Center"
                subtitle="Access student reports from here"
                background="#B5FDD5"
                imageName="home_report"
                onClick={async () => {
                  dashBoardrouting(`${REPORT}`);
                  await interactionEvent({
                    context: 'Navigation_bar',
                    name: 'REPORT_CENTER',
                    pathSegments: pathSegments,
                    isOffline: isOffline,
                  });
                }}
              />
              {sso_data?.session_mode !== 'teach' && (
                <ModuleButton
                  title="Assessment Library"
                  subtitle="Generate tests from here"
                  onClick={async () => {
                    if (class_subject_info?.subjectId) {
                      if (isClickerEnabled) {
                        setIsOpenSelectModeOfConduction(true);
                      } else {
                        dashBoardrouting(`${ASSESMENT_LIBRARY}`);
                      }
                    } else {
                      setSubjectPopupState(true);
                      setNavigateToLibrary(true);
                    }
                    // dashBoardrouting(`${ASSESMENT_LIBRARY}`);
                    await interactionEvent({
                      context: 'Navigation_bar',
                      name: 'ASSESSMENT_LIBRARY',
                      pathSegments: pathSegments,
                      isOffline: isOffline,
                    });
                  }}
                />
              )}
              {sso_data?.session_mode !== 'teach' && (
                <ModuleButton
                  title="Create Assessment"
                  subtitle="Create and Assign all types of assessments"
                  background="#DBECFF"
                  imageName="create_assess"
                  onClick={async () => {
                    assessClickHandler();
                    await interactionEvent({
                      context: 'Navigation_bar',
                      name: 'CREATE_ASSESSMENT',
                      pathSegments: pathSegments,
                      isOffline: isOffline,
                    });
                  }}
                />
              )}
              {/* <ModuleButton
              title={mobile ? 'Holistic Cards' : 'Holistic Progress Cards'}
              subtitle='Check the overall competency progress from here'
              background='#FBDDDD'
              imageName='holistic_card'
            /> */}
              <AssementSelectionPopup
                open={assessmentSelectionPopupDisplay}
                selectedValue={selectedAssessment}
                optionClickHandler={optionSelectionHandler}
                submitClickHandler={createAssessmentNavigation}
                isClickerEnabled={class_subject_info?.isClickerSupported}
                handleClose={() => setAssessmentSelectionPopupDisplay(false)}
                classInfo={`${class_subject_info?.classname}${class_subject_info?.section} - ${class_subject_info?.subject}`}
                selectedMode={selectedMode}
                setSelectedMode={setSelectedMode}
              />
            </Box>
          )}
        </Box>

        {/* <Box>{!isOfflineFrontend && <HomeLessonPlansList />}</Box> */}

        {/* Show if online, class info is not explicitly against clicker, and one class supports it */}
        {canWeShowClickerAssessments() ? (
          <HomeClickerAssessmentList
            seeAllClickHandler={async () => {
              navigate(HOMEWORK_MANAGE, {
                state: {
                  assessmentState: 'Assigned',
                  modeOfConduction: 'clicker',
                },
              });
              await interactionEvent({
                context: 'clicker_assessment_list',
                name: 'SEE_ALL',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
          />
        ) : null}

        <Box>
          {!isOfflineFrontend && sso_data?.session_mode !== 'teach' && (
            <HomeCompletedHomeworkList
              loading={assessmentListLoading}
              error={assessmentListError}
              seeAllClickHandler={async () => {
                navigate(HOMEWORK_MANAGE, {
                  state: { assessmentState: 'Ended' },
                });
                await interactionEvent({
                  context: 'completed_assessment_list',
                  name: 'SEE_ALL',
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
            />
          )}
        </Box>
        <Box>
          {!isOfflineFrontend && sso_data?.session_mode !== 'teach' && (
            <HomeHomeworksList
              loading={assessmentListLoading}
              error={assessmentListError}
              seeAllClickHandler={async () => {
                // Corrected syntax
                navigate(HOMEWORK_MANAGE, {
                  state: { assessmentState: 'Assigned' },
                });
                await interactionEvent({
                  context: 'assessment_list',
                  name: 'SEE_ALL',
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
            />
          )}
        </Box>
        {isWhiteLabelingFeature && (
          <Box sx={styles.logoContainer}>
            <ImageWrapper
              name="geneo-Home-logo"
              type="png"
              parentFolder="images"
              styles={styles.logo}
            />
          </Box>
        )}
        {/* <Box>
          <FeatureList
            feaureList={featureList}
            background={
              mobile
                ? 'linear-gradient(to bottom, white 25%,#FCF1C7 25% 50%,white 50% 100%)'
                : undefined
            }
          />
        </Box> */}

        {!isOfflineFrontend && hasPreviousYearQP ? (
          <PreviousYearQPCard />
        ) : null}

        <Box sx={{ paddingBottom: { lg: pxToRem(20) } }}>
          {!isOfflineFrontend && (
            <HomeVideoCard
              title="How to Videos"
              videos={[
                {
                  title: 'How to Log-In in Teacher',
                  duration: '4 Min',
                  videoUrl:
                    'https://storage.googleapis.com/schoolnet_app_assets/how-to-videos/teacher_login.mp4',
                  thumbnailUrl: 'app-login',
                },
                {
                  title: 'Choose and Assign Assessment',
                  duration: '3 Min',
                  videoUrl:
                    'https://storage.googleapis.com/schoolnet_app_assets/how-to-videos/teacher_assign_assessment.mp4',
                  thumbnailUrl: 'assign-assessment',
                },
                {
                  title: 'Solve and Submit Assessment',
                  duration: '2 Min',
                  videoUrl:
                    'https://storage.googleapis.com/schoolnet_app_assets/how-to-videos/Student-submit%20assessment.mp4',
                  thumbnailUrl: 'assessment-submit',
                },
              ]}
              linkButtonHandler={() => console.log('See All clicked')}
              itemsToShow={4}
              containerMdWidth="100%"
              totalNoOfItems={3}
            />
          )}
        </Box>

        {!isOfflineFrontend ? (
          <Box
            sx={{
              p: { xs: pxToRem(18), md: pxTovW(0) },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: pxToRem(10) },
            }}
          >
            <Typography variant="h2" mb={pxTovW(22)}>
              Performance Snapshot
            </Typography>

            <TeacherHomeWebCarousel />
          </Box>
        ) : null}

        <Box>
          {!isOfflineFrontend && sso_data?.session_mode !== 'assess' && (
            <HomeLessonPlansList />
          )}
        </Box>
      </Box>

      <ClassAndSubjectPopup
        modalState={subjectPopupState}
        setModalState={setSubjectPopupState}
        displayData={subjectsWithClass}
        classSubjectsList={user_info?.teachClassSubjects}
        classAndsubjectClickHandler={classAndsubjectClickHandler}
      />

      <FASelectMultiSubjectsPopup
        open={faMultiSubjectsPopup}
        handleClose={() => {
          setFAMultiSubjectsPopup(false);
          setSelectedAssessment(undefined);
        }}
      />

      <QPGSelectMultiSubjectsPopup
        open={qpgSelectMultiSubjectsPopup}
        handleClose={() => setQPGSelectMultiSubjectsPopup(false)}
      />

      {isClickerEnabled && (
        <Dialog
          open={isOpenSelectModeOfConduction}
          onClose={() => {
            setIsOpenSelectModeOfConduction(false);
            dispatch(setModeOfConduction(getModeOfConductionEnum('Geneo App')));
          }}
          maxWidth={false} // Important to override default sizes like "md"
          fullWidth={false} // Prevent it from automatically stretching
          PaperProps={{
            sx: {
              width: { xs: '77%', md: pxTovW(950), lg: pxTovW(780) }, // custom responsive width
              borderRadius: pxToRem(12),
              position: 'relative',
              overflow: 'visible',
            },
          }}
        >
          <IconButton
            onClick={() => {
              setIsOpenSelectModeOfConduction(false);
              dispatch(
                setModeOfConduction(getModeOfConductionEnum('Geneo App'))
              );
            }}
            sx={{
              position: 'absolute',
              top: pxToRem(3),
              right: pxToRem(-45),
              backgroundColor: '#FFD700', // Yellow background like your screenshot
              color: 'white',
              '&:hover': {
                backgroundColor: '#FFC107',
              },
              zIndex: '100',
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              paddingX: { xs: pxToRem(20), md: pxTovW(89) },
              paddingY: { xs: pxToRem(20), md: pxTovW(68) },
            }}
          >
            <ConductionButtonsComponent
              value={
                selectedMode === '' && class_subject_info?.isClickerSupported
                  ? 'clicker'
                  : selectedMode === 'Geneo Clicker'
                  ? 'clicker'
                  : 'mobile-app'
              }
              onChange={async (val: 'mobile-app' | 'clicker') => {
                const modeString =
                  val === 'mobile-app' ? 'Geneo App' : 'Geneo Clicker';
                setSelectedMode(modeString);
                await interactionEvent({
                  context: 'Choose_mode_of_conduction',
                  name: modeString.toUpperCase().replace(' ', '_'),
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: { xs: pxToRem(20), md: pxTovW(40) },
              }}
            >
              <Box
                sx={{
                  width: { xs: pxToRem(230), md: pxTovW(650), lg: pxTovW(497) },
                }}
              >
                <PrimaryButton
                  fullWidth
                  onClick={async () => {
                    // const finalMode = selectedMode || 'Geneo App'; // fallback
                    // dispatch(
                    //   setModeOfConduction(getModeOfConductionEnum(finalMode))
                    // );
                    dispatch(
                      setModeOfConduction(
                        selectedMode === '' &&
                          class_subject_info?.isClickerSupported
                          ? ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
                          : selectedMode === 'Geneo Clicker'
                          ? ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
                          : ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_APP
                      )
                    );
                    // setSelectedMode(finalMode);
                    dashBoardrouting(`${ASSESMENT_LIBRARY}`);
                    await interactionEvent({
                      context: 'Choose_mode_of_conduction',
                      name: 'PROCEED',
                      pathSegments: pathSegments,
                      isOffline: isOffline,
                    });
                  }}
                >
                  PROCEED
                </PrimaryButton>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {/* {deviceType === 'web' && <Notification />} */}
    </Box>
  );
};

export default Home;

/*

<Box sx={{ display: 'flex', gap: pxTovW(28) }}>
  {Array.from({ length: 3 }).map(() => (
    <ImageWrapper
      name="Group41484"
      type="png"
      parentFolder="tempAssets"
      styles={{
        width: pxTovW(446),
        height: pxTovW(215),
        objectFit: 'cover',
        borderRadius: pxTovW(30),
        // boxShadow: `0px 0px ${pxTovW(10)} #E7E7E7D9`,
      }}
    />
  ))}
</Box>

*/
